// Avatar chip for a team_member, identified by Slack user id.
//
// Renders an actual `<img>` when `team_members.avatar_url` is populated
// (synced from Slack or Google OAuth metadata). Falls back to a rounded
// initials chip when the URL is missing OR the image fails to load.
//
// Two robustness fixes (2026-05) after Google-hosted avatars showed as
// broken/blank on the dashboard while Slack ones worked:
//   1. `normalizeAvatarUrl` — bare Google `lh3.googleusercontent.com/a/…`
//      URLs are unreliable when hotlinked (intermittent 403/429 and no
//      explicit size). Appending a size segment (`=s96-c`) requests the
//      documented fixed-size crop, which loads reliably and is smaller.
//   2. `onError` → initials. Previously a failed image left the browser's
//      broken-image glyph forever. Now any load failure (dead URL,
//      revoked Google photo, network) degrades cleanly to initials —
//      the same fallback as "no avatar at all".
//
// Used by every initials-chip site (TicketCard, TicketActivitySidebar,
// NotesPanel, TopNav, Team page) so chip styling stays consistent.

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { assigneeInitials, assigneeName } from '@/lib/format';

export interface UserAvatarProps {
  slackId: string | null | undefined;
  teamMembersBySlackId?: Map<string, { name?: string; avatar_url?: string | null }>;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<UserAvatarProps['size']>, string> = {
  xs: 'h-4 w-4 text-3xs',
  sm: 'h-5 w-5 text-3xs',
  md: 'h-7 w-7 text-xs',
  lg: 'h-9 w-9 text-sm',
};

/** Google OAuth profile photos come back as either a bare
 *  `https://lh3.googleusercontent.com/a/<id>` or one already carrying a
 *  size segment (`…=s96-c`). The bare form is unreliable when hotlinked
 *  from another origin — append a fixed-size crop so the CDN serves the
 *  stable, cacheable variant. Non-Google URLs (Slack `avatars.slack-edge.com`,
 *  etc.) are returned untouched — they already work. */
function normalizeAvatarUrl(url: string): string {
  if (!url.includes('googleusercontent.com')) return url;
  // Already has a size/crop segment after the last path part?
  // Google encodes it as `=s96-c` / `=s400` etc. at the very end.
  if (/=s\d+(-c)?$/.test(url) || /=w\d+-h\d+/.test(url)) return url;
  return url + '=s96-c';
}

export function UserAvatar({
  slackId,
  teamMembersBySlackId,
  size = 'sm',
  className,
}: UserAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);

  const member = slackId ? teamMembersBySlackId?.get(slackId) : undefined;
  const rawAvatar = member?.avatar_url ?? null;

  // Reset the failed flag if the underlying URL changes (e.g. avatar
  // backfilled while the page is open, or list re-renders with a
  // different member in this slot).
  useEffect(() => {
    setImgFailed(false);
  }, [rawAvatar]);

  if (!slackId) {
    return <span className={cn('inline-block', SIZE_CLASSES[size], className)} aria-hidden />;
  }

  const name = assigneeName(slackId, teamMembersBySlackId);
  const initials = assigneeInitials(slackId, teamMembersBySlackId);
  const avatarUrl = rawAvatar ? normalizeAvatarUrl(rawAvatar) : null;

  if (avatarUrl && !imgFailed) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        title={name}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setImgFailed(true)}
        className={cn(
          'inline-block shrink-0 rounded-full bg-secondary object-cover',
          SIZE_CLASSES[size],
          className,
        )}
      />
    );
  }

  return (
    <span
      title={name}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-secondary font-medium text-secondary-foreground',
        SIZE_CLASSES[size],
        className,
      )}
    >
      {initials}
    </span>
  );
}
